<template>
  <template v-if="$route.name === 'employees.index'">
    <EmployeeFilter
      @search="handleSearch"
      @gender="handleGenderFilter"
      @add="navigateToAdd"
      @reset-filters="handleResetFilters"
    />

    <div class="card-body">
      <EmployeeTable
        :employees="employees"
        :loading="isLoading"
        @edit-employee="navigateToEdit"
        @delete-employee="openDeleteDialog"
      />
    </div>

    <PaginationControls
      v-if="!isLoading && pagination.totalPages > 1"
      :pagination="pagination"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />

    <DeleteEmployeeDialog ref="deleteDialog" @delete="handleDeleteEmployee" />
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import EmployeeTable from "@/components/features/employees/EmployeeTable.vue";
import EmployeeFilter from "@/components/features/employees/filters/EmployeeFilter.vue";
import PaginationControls from "@/components/common/PaginationControls.vue";
import DeleteEmployeeDialog from "@/components/features/employees/dialogs/DeleteEmployeeDialog.vue";

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const toast = useToast();

const deleteDialog = ref(null);
const isLoading = ref(false);

const employees = computed(() => store.state.employee.employees);
const pagination = computed(() => store.state.employee.pagination);

const fetchEmployees = async (page = 1, perPage = 10) => {
  isLoading.value = true;
  try {
    await store.dispatch("employee/fetchEmployees", {
      page,
      per_page: perPage,
      search: store.state.employee.filters.search,
      gender: store.state.employee.filters.gender,
    });
  } catch (error) {
    toast.error(t("employees.load_failed"));
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = (searchTerm) => {
  store.commit("employee/SET_FILTERS", { ...store.state.employee.filters, search: searchTerm });
  fetchEmployees(1);
};

const handleGenderFilter = (gender) => {
  store.commit("employee/SET_FILTERS", { ...store.state.employee.filters, gender });
  fetchEmployees(1);
};

const handleResetFilters = () => {
  store.commit("employee/SET_FILTERS", { search: "", gender: null });
  fetchEmployees(1);
};

const handlePageChange = (page) => fetchEmployees(page, pagination.value.perPage);
const handlePerPageChange = (perPage) => fetchEmployees(1, perPage);

const navigateToAdd = () => router.push({ name: "employees.create" });
const navigateToEdit = (employee) => router.push({ name: "employees.edit", params: { id: employee.id } });

const openDeleteDialog = (employee) => deleteDialog.value?.open(employee);

const handleDeleteEmployee = async (employeeId) => {
  try {
    await store.dispatch("employee/deleteEmployee", employeeId);
    toast.success(t("employees.deleted_successfully"));
    if (employees.value.length === 0 && pagination.value.currentPage > 1) {
      await fetchEmployees(pagination.value.currentPage - 1, pagination.value.perPage);
    } else {
      await fetchEmployees(pagination.value.currentPage, pagination.value.perPage);
    }
  } catch (error) {
    toast.error(error?.response?.data?.message || t("employees.delete_failed"));
  }
};

onMounted(() => fetchEmployees());
</script>
